import { api } from '../lib/api';
import { UrlContextMetadataItem } from '../types';

/**
 * ============================================================================
 * A CENTRAL DE CONHECIMENTO PASSOU A FALAR PELO CONTRATO
 * ============================================================================
 *
 * O QUE ESTAS DUAS FUNÇÕES FAZIAM
 *
 *     await fetch('/api/gemini/generate-with-urls', { ... })
 *
 * `/api/gemini/*` era servido pelo host Express do próprio painel
 * (`server.ts`), que mantinha um SEGUNDO cliente do Gemini:
 *
 *     const geminiAi = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY || '' });
 *
 * Três problemas, todos deste repositório:
 *
 *  1. `|| ''` é fallback de credencial — proibido pelo `CLAUDE.md` pela razão de
 *     sempre: sem a variável, o processo subia e falhava depois, em runtime, com
 *     uma mensagem do Google que não diz que faltou configuração.
 *  2. Fora do contrato: sem `@Rota()`, sem guard, sem nível de acesso. Qualquer
 *     um que alcançasse o host do painel gastava a chave, logado ou não.
 *  3. Sem medição. Toda chamada da base de conhecimento gastava tokens que não
 *     apareciam em relatório nenhum — e agora que existe um relatório, essa
 *     porta faria o total mentir para menos, que é a pior direção do erro.
 *
 * Agora as duas passam por `ia.gerar`, a mesma rota do copiloto do editor
 * (`AiSidebar.tsx`), com `urlsDeContexto` ligando a ferramenta `url_context` do
 * provedor — o modelo continua LENDO as páginas, não apenas vendo os endereços.
 */

export interface GeminiResponse {
  text: string;
  urlContextMetadata?: UrlContextMetadataItem[];
}

/** Traduz o formato do contrato para o que as telas já consomem. */
function paraMetadadoDeUrl(
  lidas: Array<{ url: string; status: string }>,
): UrlContextMetadataItem[] | undefined {
  if (lidas.length === 0) return undefined;
  return lidas.map((u) => ({ retrievedUrl: u.url, urlRetrievalStatus: u.status }));
}

export const generateContentWithUrlContext = async (
  clienteId: string,
  prompt: string,
  urls: string[],
): Promise<GeminiResponse> => {
  const resposta = await api.chamar(
    'ia.gerarParaCliente',
    {
      params: { id: clienteId },
      corpo: {
        tarefa: 'texto-livre',
        origem: 'painel',
        partes: [{ tipo: 'texto', texto: prompt }],
        ...(urls.length > 0 ? { urlsDeContexto: urls } : {}),
      },
    },
    // O modelo BUSCA as páginas antes de responder; isso não cabe em 30 s.
    { timeoutMs: 130_000 },
  );

  return {
    text: resposta.texto,
    urlContextMetadata: paraMetadadoDeUrl(resposta.urlsLidas),
  };
};

/**
 * Sugestões de pergunta a partir das URLs da base.
 *
 * Devolve as sugestões JÁ SEPARADAS, e não uma string JSON para a tela parsear.
 * A versão anterior pedia ao modelo um objeto JSON e fazia `JSON.parse` no
 * navegador dentro de um `try` que zerava a lista no erro: uma vírgula a mais na
 * resposta do modelo apagava as sugestões sem nenhum sintoma. A instrução de
 * sistema da tarefa `sugestoes` já pede uma por linha — separar por linha não
 * tem como falhar pela metade.
 */
export const getInitialSuggestions = async (
  clienteId: string,
  urls: string[],
): Promise<string[]> => {
  if (urls.length === 0) return [];

  const resposta = await api.chamar(
    'ia.gerarParaCliente',
    {
    params: { id: clienteId },
    corpo: {
      tarefa: 'sugestoes',
      origem: 'painel',
      urlsDeContexto: urls,
      partes: [
        {
          tipo: 'texto',
          texto:
            'Com base no conteúdo das páginas de documentação informadas, escreva de 3 a 4 ' +
            'perguntas curtas e acionáveis que uma pessoa faria para explorar esses ' +
            'documentos. Uma por linha, sem numeração e sem marcador.',
        },
      ],
    },
    },
    { timeoutMs: 130_000 },
  );

  return resposta.texto
    .split('\n')
    .map((l) => l.replace(/^[-*\d.)\s]+/, '').trim())
    .filter((l) => l.length > 0)
    .slice(0, 5);
};

/**
 * Uma resposta em JSON, pedida ao modelo pela rota do contrato.
 *
 * Existe para os dois geradores que ainda falavam com o Gemini pelo host Express
 * do painel (`/api/generate-ads` e `/api/generate-social-content`). Os dois
 * liam `process.env.GEMINI_API_KEY` num segundo lugar — então gravar a chave
 * pela tela de IA não teria efeito nenhum sobre eles, e todo token que
 * gastavam ficava fora do relatório de custo.
 *
 * A ROTA É A DO CLIENTE, e isso não é detalhe: `TrafficManagement` e
 * `SocialMediaDashboard` são abas do `ClientDashboard`, que roda em `#/painel`
 * com a sessão de PORTAL. `ia.gerar` é `acesso: 'operador'` — chamá-la de lá
 * daria 403 e as duas telas simplesmente parariam de funcionar para quem elas
 * atendem. `ia.gerarParaCliente` é `acesso: 'cliente'` com
 * `dono: 'cliente:params.id'`: o cliente só gera para si, o guard confere, e o
 * gasto cai na linha dele. O operador e o worker também alcançam essa rota (o
 * `case 'cliente'` do guard os aceita), então o suporte continua abrindo a tela.
 *
 * O `clienteId` vai no CAMINHO, nunca no corpo: campo falsificável ao lado de
 * caminho conferido é como o cliente A atribuiria o gasto ao cliente B.
 */
export async function gerarJson<T>(prompt: string, clienteId: string): Promise<T> {
  const resposta = await api.chamar(
    'ia.gerarParaCliente',
    {
      params: { id: clienteId },
      corpo: {
        tarefa: 'texto-livre',
        origem: 'painel',
        partes: [{ tipo: 'texto', texto: prompt }],
      },
    },
    // Geração longa: a API espera até 100 s pelo provedor. Ver o comentário de
    // `OpcoesDaChamada` no cliente do contrato.
    { timeoutMs: 130_000 },
  );

  // O modelo devolve o JSON dentro de cercas de markdown com frequência, mesmo
  // instruído a não fazer isso. Limpar aqui, uma vez, evita o mesmo `replace`
  // repetido em cada tela — e evita que uma delas esqueça.
  const limpo = resposta.texto
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .trim();

  try {
    return JSON.parse(limpo) as T;
  } catch {
    throw new Error(
      'A IA respondeu num formato que não é JSON. Tente de novo; se persistir, ' +
        'troque o modelo na tela de Inteligência Artificial.',
    );
  }
}
